/**
 * "One ledger" text-translate transport: streams `POST /translate` on the
 * baba-v2 mobile gateway (the SAME root-mounted endpoint the phone calls —
 * see `mobile/services/translationService.ts`) so a signed-in web user's
 * text translations charge the same `character_usage` ledger and land in
 * the same `translations` table as the phone, instead of apps/api's
 * separate guest/free quota.
 *
 * `/translate` and `/translate/cancel` are mounted at the Worker ROOT, not
 * under `/api/v1` — `../gateway.ts` only targets the `/api/v1` base, so this
 * derives the root origin the exact same way
 * `../../src/components/chat/gatewayChat.ts`'s `CHAT_WORKER_ORIGIN` does,
 * reusing `../gateway.ts` for the base URL and the auth-error classes rather
 * than re-deriving `VITE_BABA_GATEWAY_URL` parsing a third time.
 */
import { BABA_GATEWAY_BASE_URL } from '../gateway'
import { getAuthToken } from '../baba-auth'
import type { TranslateError, TranslateOptions, UsageSnapshot } from '../baba-api'
import { buildGatewayTranslateBody, type GatewayTranslateBodyInput, parseGatewaySseLine } from './mapping'

/** Worker root — `BABA_GATEWAY_BASE_URL` minus its `/api/v1` suffix. Mirrors
 *  `CHAT_WORKER_ORIGIN` in `gatewayChat.ts`. */
export const TRANSLATE_WORKER_ORIGIN = BABA_GATEWAY_BASE_URL.replace(/\/api\/v1\/?$/, '')

const TRANSLIT_SEPARATOR = '∰'

/**
 * How long the stream may go SILENT before it is given up: no response yet, or
 * no new bytes since the last chunk. An inactivity limit rather than a total
 * one, because a long Pro translation (20,000 characters) streams for well over
 * a minute and a flat 60 s cut it off mid-sentence. Matches the apps/api path's
 * `translateStream` in `../baba-api.ts`.
 */
export const STREAM_IDLE_TIMEOUT_MS = 60_000

/** What the user reads when the stream stopped partway through. */
export const STREAM_STALLED_MESSAGE = 'The translation timed out before it finished. Try again.'
/** Short bound on the best-effort cancel notification, matching
 *  `notifyServerCancellation` in baba-v2 `mobile/services/translationManager.ts`. */
const CANCEL_TIMEOUT_MS = 5_000
/** Short bound on the best-effort prefetch ping — see `prefetchViaGateway`.
 *  Nothing waits on this request, so it only needs to be short enough not to
 *  pile up sockets if the gateway is slow to respond. */
const PREFETCH_TIMEOUT_MS = 5_000

export type GatewayUnavailableReason = 'auth' | 'network' | 'server_error'

export type GatewayTranslateOutcome =
  /** Handled entirely on the gateway — including a genuine rejection (limit,
   *  429, pro-required) already delivered through `onError`. The caller must
   *  NOT fall back to apps/api in this case: that would let a user who is
   *  over THIS ledger's limit bypass it through the separate apps/api quota. */
  | { status: 'ok' }
  /** The gateway itself could not be reached or authenticated — a 401 (the
   *  caller has no valid `baba_web`-issued session yet, e.g.
   *  `WEB_JWT_SECRET` not deployed), a network failure, or a 5xx. Guaranteed
   *  to have called neither `onToken` nor `onError`, so the caller can fall
   *  back to the apps/api path exactly as if this had never run. */
  | { status: 'unavailable'; reason: GatewayUnavailableReason }

/**
 * Pure routing decision extracted for unit testing — the actual call site
 * (`translateStream` in `../baba-api.ts`) computes both inputs itself
 * (`isSignedIn()` and the shared gateway probe status from
 * `../library/useLibrarySource.ts`).
 */
export function shouldRouteThroughGateway(opts: {
  signedIn: boolean
  gatewayStatus: 'checking' | 'gateway' | 'fallback'
}): boolean {
  return opts.signedIn && opts.gatewayStatus === 'gateway'
}

function freshJobId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  // Test/SSR fallback only — a real browser always has crypto.randomUUID.
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/** Best-effort `POST /translate/cancel` so an aborted web translation frees
 *  the same in-flight upstream slot an abandoned phone translation would.
 *  Never throws — mirrors baba-v2 `translationManager.ts#notifyServerCancellation`. */
function notifyGatewayCancel(jobId: string, token: string): void {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), CANCEL_TIMEOUT_MS)
  fetch(`${TRANSLATE_WORKER_ORIGIN}/translate/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ jobId }),
    signal: controller.signal,
  })
    .catch(() => {})
    .finally(() => clearTimeout(timeout))
}

function classifyPreStreamFailure(status: number): GatewayUnavailableReason | null {
  if (status === 401) return 'auth'
  if (status >= 500) return 'server_error'
  return null
}

/** The prompt-shaping fields common to a real translation and a prefetch
 *  ping — shared so `translateViaGateway` and `prefetchViaGateway` build the
 *  request body from the exact same reading of `opts` and can never drift
 *  from each other on what the gateway is asked to translate. */
function gatewayPromptFields(
  opts: Pick<TranslateOptions, 'text' | 'sourceLang' | 'targetLang' | 'gender' | 'genderContext' | 'mode' | 'model' | 'context' | 'style'>,
): Omit<GatewayTranslateBodyInput, 'jobId' | 'audienceChangeFromId'> {
  return {
    text: opts.text,
    sourceLang: opts.sourceLang,
    targetLang: opts.targetLang,
    gender: opts.gender,
    genderContext: opts.genderContext,
    mode: opts.mode,
    model: opts.model,
    context: opts.context,
    style: opts.style,
  }
}

/**
 * Streams a text translation through the gateway, driving the SAME
 * `TranslateOptions` callback contract `translateStream` (`../baba-api.ts`)
 * already exposes to its callers (`onToken`, `onTransliteration`, `onUsage`,
 * `onDone`, `onError`) — so nothing downstream of `translateStream` (
 * `runTextTranslation` in `runTranslateStreams.ts`, `Translator.tsx`) needs
 * to know which backend actually served a given call.
 */
export async function translateViaGateway(opts: TranslateOptions): Promise<GatewayTranslateOutcome> {
  const token = getAuthToken()
  if (!token) return { status: 'unavailable', reason: 'auth' }

  const jobId = freshJobId()
  const body = buildGatewayTranslateBody({
    ...gatewayPromptFields(opts),
    audienceChangeFromId: opts.audienceChangeFromId,
    jobId,
  })

  const timeoutCtrl = new AbortController()
  let timeoutId = setTimeout(() => timeoutCtrl.abort(), STREAM_IDLE_TIMEOUT_MS)
  /** Every chunk proves the stream is alive; restart the silence clock. */
  const stillAlive = () => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => timeoutCtrl.abort(), STREAM_IDLE_TIMEOUT_MS)
  }
  const externalSignal = opts.signal
  const onExternalAbort = () => {
    notifyGatewayCancel(jobId, token)
    timeoutCtrl.abort()
  }
  if (externalSignal) {
    if (externalSignal.aborted) onExternalAbort()
    else externalSignal.addEventListener('abort', onExternalAbort, { once: true })
  }

  let response: Response
  try {
    response = await fetch(`${TRANSLATE_WORKER_ORIGIN}/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: timeoutCtrl.signal,
    })
  } catch (err) {
    clearTimeout(timeoutId)
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort)
    const e = err as Error
    if (e.name === 'AbortError' && externalSignal?.aborted) return { status: 'ok' } // user-initiated cancel
    return { status: 'unavailable', reason: 'network' }
  }

  if (!response.ok) {
    clearTimeout(timeoutId)
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort)
    const unavailable = classifyPreStreamFailure(response.status)
    if (unavailable) return { status: 'unavailable', reason: unavailable }

    // A genuine, settled answer from the SAME ledger (per-translation/period
    // limit, summary-mode paywall, malformed request, rate limit) — surface
    // it and tell the caller NOT to fall back to apps/api's separate quota.
    const data = await response.json().catch(() => ({}) as Record<string, unknown>)
    const message = typeof data.error === 'string' ? data.error : `Translation failed (${response.status})`
    const err: TranslateError =
      response.status === 429 || /character limit exceeded/i.test(message)
        ? { type: 'limit_reached', message }
        : { type: 'error', message }
    opts.onError?.(err)
    return { status: 'ok' }
  }

  if (!response.body) {
    clearTimeout(timeoutId)
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort)
    opts.onError?.({ type: 'error', message: 'No response body from server' })
    return { status: 'ok' }
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let inTransliteration = false
  let settled = false
  /** True once at least one delta reached `onToken`/`onTransliteration` —
   *  gates whether a later stream-read failure can still fall back to
   *  apps/api (nothing shown to the user yet) or must surface as a real
   *  error (falling back now would duplicate/interrupt visible output). */
  let anyTokenEmitted = false
  let earlyUnavailable: GatewayUnavailableReason | null = null

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      stillAlive()
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      for (const line of lines) {
        const event = parseGatewaySseLine(line)
        if (!event) continue

        if (event.type === 'delta') {
          // The delta stream can carry the SAME ∰ transliteration separator
          // apps/api's stream does (see baba-v2
          // server/translate/utils/transliteration.ts) — split it the same
          // way `translateStream` in `../baba-api.ts` already does for that
          // path, so onToken/onTransliteration split identically regardless
          // of which backend served the request.
          const text = event.delta
          if (!text) continue
          anyTokenEmitted = true
          if (text.includes(TRANSLIT_SEPARATOR)) {
            const [before, ...rest] = text.split(TRANSLIT_SEPARATOR)
            const after = rest.join(TRANSLIT_SEPARATOR)
            const beforeTrim = before.replace(/\s+$/, '')
            if (beforeTrim) {
              if (inTransliteration) opts.onTransliteration?.(beforeTrim)
              else opts.onToken?.(beforeTrim)
            }
            inTransliteration = true
            const afterTrim = after.replace(/^\s+/, '')
            if (afterTrim) opts.onTransliteration?.(afterTrim)
          } else if (inTransliteration) {
            opts.onTransliteration?.(text)
          } else {
            opts.onToken?.(text)
          }
        } else if (event.type === 'done') {
          settled = true
          const usage: UsageSnapshot = { charsUsed: event.updatedUsage, charsLimit: event.periodLimit }
          opts.onUsage?.(usage)
          opts.onDone?.({
            source: 'gateway',
            historyId: event.historyId,
            translation: event.translation,
            transliteration: event.transliteration,
            charCount: event.charCount,
            isOverLimit: event.isOverLimit,
            isPro: event.isPro,
            updatedUsage: event.updatedUsage,
            periodLimit: event.periodLimit,
          })
        } else if (event.type === 'error') {
          settled = true
          const limitLike = /character limit exceeded/i.test(event.message) || event.message === 'cancelled'
          if (event.message !== 'cancelled') {
            opts.onError?.(limitLike ? { type: 'limit_reached', message: event.message } : { type: 'error', message: event.message })
          }
        }
      }
    }
  } catch (err) {
    const e = err as Error
    const userCancelled = e.name === 'AbortError' && externalSignal?.aborted
    if (userCancelled) {
      // Explicit user cancel — already handled (best-effort cancel notice
      // fired from the abort listener above); no error, no fallback.
      settled = true
    } else if (!anyTokenEmitted) {
      // Nothing reached the user yet (e.g. our own silence timeout with the
      // connection stuck after a 200), so it is safe to hand this back to
      // `translateStream` for an apps/api retry instead of surfacing a raw
      // abort/network error.
      earlyUnavailable = 'network'
    } else {
      // Partway through: the text on screen is incomplete, and the caller
      // must not record it as a finished translation (no onDone below).
      const stalled = e.name === 'AbortError' || timeoutCtrl.signal.aborted
      opts.onError?.({ type: 'error', message: stalled ? STREAM_STALLED_MESSAGE : e.message || 'Translation failed' })
      settled = true
    }
  } finally {
    clearTimeout(timeoutId)
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort)
    if (!settled && !earlyUnavailable) opts.onDone?.({ source: 'gateway' })
  }

  if (earlyUnavailable) return { status: 'unavailable', reason: earlyUnavailable }

  return { status: 'ok' }
}

export type PrefetchTrigger = 'paste' | 'pause'

export type PrefetchOptions = Pick<
  TranslateOptions,
  'text' | 'sourceLang' | 'targetLang' | 'gender' | 'genderContext' | 'mode' | 'model' | 'context' | 'style'
>

/**
 * Fire-and-forget "warm the cache" ping to the SAME `/translate` gateway
 * endpoint `translateViaGateway` streams from — same prompt-shaping fields
 * (built through the same `gatewayPromptFields` helper, so it cannot drift
 * from the real request), plus `prefetch: true` and `prefetchTrigger`. Omits
 * `jobId` and `audienceChangeFromId`: a prefetch is not a job the server
 * needs to track, and never carries a continuity baseline. Never throws and
 * never reports success/failure — the eventual real translation (whichever
 * backend actually serves it) is the only outcome that matters, and the
 * caller (`Translator.tsx`'s prefetch effect) never awaits this beyond
 * firing it.
 */
export async function prefetchViaGateway(opts: PrefetchOptions, trigger: PrefetchTrigger): Promise<void> {
  const token = getAuthToken()
  if (!token) return

  // `buildGatewayTranslateBody` requires a jobId — a prefetch has none to
  // give it, so this builds with a throwaway value and strips it below
  // rather than widening the real-request contract to make jobId optional.
  const { jobId: _jobId, ...body } = buildGatewayTranslateBody({ ...gatewayPromptFields(opts), jobId: 'prefetch' })
  const prefetchBody = { ...body, prefetch: true, prefetchTrigger: trigger }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), PREFETCH_TIMEOUT_MS)
  try {
    await fetch(`${TRANSLATE_WORKER_ORIGIN}/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(prefetchBody),
      signal: controller.signal,
    })
  } catch {
    // Best-effort only — swallow network errors, timeouts, everything.
  } finally {
    clearTimeout(timeoutId)
  }
}
